import { createContext, useContext } from 'react';

import type { TrackingLibrary } from '../utils/tracking';

import { BrazeContext } from './braze-provider';

type ContentCard = {
  aspectRatio: number;
  categories: string[];
  clicked: boolean;
  created: string | null;
  description: string;
  dismissed: boolean;
  dismissible: boolean;
  expiresAt: string;
  extras: {
    button_2_url?: string;
    button_2_text?: string;
    placement: string;
    subline?: string;
    type?: string;
  };
  id: string;
  imageUrl: string;
  linkText?: string;
  pinned: boolean;
  Tc: string | null;
  test: boolean;
  title: string;
  updated: string;
  url: string;
  viewed: boolean;
  W: string | null;
};

type ContentCardState =
  | { status: 'waiting'; cards: ContentCard[] }
  | { status: 'received'; cards: ContentCard[] };

type ContentCardContextContent = ContentCardState & {
  tracking?: TrackingLibrary;
};

interface ContentCardProviderProps {
  children: React.ReactNode;
  contentCardState: ContentCardState;
}

const ContentCardContext = createContext<ContentCardContextContent>({
  status: 'waiting',
  cards: [],
});

const useContentCards = (): ContentCardContextContent => {
  const contentCardState = useContext(ContentCardContext);
  if (contentCardState === null) {
    throw new Error('useContentCards must be used within a BrazeProvider tag');
  }
  const { trackingLibrary } = useContext(BrazeContext);
  return Object.assign({}, contentCardState, { tracking: trackingLibrary });
};

const ContentCardProvider = ({
  children,
  contentCardState,
}: ContentCardProviderProps): JSX.Element => {
  return (
    <ContentCardContext.Provider value={contentCardState}>
      {children}
    </ContentCardContext.Provider>
  );
};

export {
  useContentCards,
  ContentCardProvider,
  ContentCardContext,
  ContentCardContextContent,
  ContentCardState,
  ContentCard,
};
